import React, { useContext, useState } from 'react';
import { Box, Button, Chip, SxProps } from '@mui/material';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import {
  GetInterestGroupsDocument,
  IAddInterestResult,
  IGetInterestGroupsQuery,
  IInterest,
  IInterestGroup,
  useAddInterestToGroupMutation,
  useDeleteInterestMutation
} from '../../generated/graphql';
import { addInterestsToCache } from '../../utils/clientUtils';
import { Colors } from '../../utils/theme';
import CancelButton from '../Common/Buttons/CancelButton';
import SaveButton from '../Common/Buttons/SaveButton';
import InputComponent from '../Common/Fields/InputComponent';
import DialogBox from '../Common/Layout/DialogBox';

type IProps = {
  id: string;
  name: string;
  interests: IInterest[] | undefined;
  handleClose: () => void;
};

type NewInterest = IInterest & {
  isNew?: boolean;
};

const chipStyles: SxProps = {
  color: Colors.main,
  fontSize: 14,
  background: '#69AAF333',
  '&:active, &:focus': {
    background: '#69AAF333'
  }
};

const newChipStyles: SxProps = {
  color: Colors.skyBlue,
  background: Colors.white,
  fontSize: 14,
  border: '1px solid ' + Colors.skyBlue,
  '&:active, &:focus': {
    background: Colors.white
  }
};

const deleteIconStyles: SxProps = {
  color: Colors.skyBlue,
  width: 28,
  height: 28,
  '&:hover': {
    color: Colors.main
  }
};

const AddCategoryInterests: React.FunctionComponent<IProps> = ({
  id,
  name,
  interests,
  handleClose
}: IProps) => {
  const { dispatch } = useContext(AppContext);
  const [addInterestToCategory, { loading }] = useAddInterestToGroupMutation();
  const [newInterest, setNewInterest] = useState('');
  const [items, setItems] = useState<NewInterest[]>(interests ?? []);
  const [newItems, setNewItems] = useState<NewInterest[]>([]);
  const [deleteInterestMutation] = useDeleteInterestMutation();

  const handleCancelPressed = () => {
    if (newItems.length > 0) {
      dispatch({
        type: Actions.ShowConfirmation,
        payload: {
          text: 'There are unsaved changes. Are you sure you want to proceed?',
          agreeAction: closeDialog
        }
      });
    } else {
      handleClose();
    }
  };

  const handleSavePressed = () => {
    if (newItems.length > 0) {
      const interestsArray = newItems.map((i) => i.name);
      addInterestToCategory({
        variables: {
          input: {
            category_id: id,
            new_values: interestsArray
          }
        },
        update(_, { data }) {
          setNewItems([]);
          const updatedInterests = [] as NewInterest[];
          if (data && data.result) {
            addInterestsToCache(id, data?.result as IAddInterestResult[]);
          }
          items.forEach((item) => {
            const index = Number(data?.result.findIndex((i) => i?.name === item.id));
            if (index > -1) {
              updatedInterests.push({
                id: Number(data?.result[index]?.id).toString(),
                name: data?.result[index]?.name as string,
                interest_group: { id } as IInterestGroup
              });
            } else {
              updatedInterests.push(item);
            }
          });
          setItems(updatedInterests);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Interests saved successfully'
            }
          });
        }
      }).catch((e) =>
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message,
            autoHide: 6000
          }
        })
      );
    }
  };

  const handleDeleteInterestPressed = (item: NewInterest) => {
    const { id, isNew } = item;
    if (isNew) {
      setItems(items.filter((i) => i.id !== id));
      setNewItems(newItems.filter((i) => i.id !== id));
    } else {
      dispatch({
        type: Actions.ShowConfirmation,
        payload: {
          text: `Are you sure you want to delete ${item.name} from ${name
            .replace(/([A-Z][a-z])/g, ' $1')
            .replace(/(\d)/g, ' $1')} category?`,
          params: item,
          agreeAction: handleDeleteInterestItem
        }
      });
    }
  };

  const handleDeleteInterestItem = (interestItem: NewInterest) => {
    dispatch({
      type: Actions.HideConfirmation
    });
    deleteInterestMutation({
      variables: {
        id: parseInt(interestItem?.id)
      },
      update(cache) {
        const interestGroups = cache.readQuery({
          query: GetInterestGroupsDocument
        }) as IGetInterestGroupsQuery;
        if (interestGroups.result) {
          const data = interestGroups.result.map((gr) => {
            if (gr?.id === interestItem.interest_group.id) {
              return { ...gr, interests: gr.interests.filter((i) => i?.id !== interestItem.id) };
            } else {
              return gr;
            }
          });
          cache.writeQuery({
            query: GetInterestGroupsDocument,
            data: { result: data }
          });
        }
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            text: `Interest ${interestItem.name} removed from ${name} category`,
            severity: 'success'
          }
        });
      }
    });
  };

  const handleAddInterestPressed = () => {
    if (newInterest.length > 0) {
      const canAddInterest = canAddNewInterest(newInterest.trim());
      if (canAddInterest) {
        const item = {
          id: newInterest,
          name: newInterest.trim(),
          interest_group_id: id,
          isNew: true,
          interest_group: { id, name: '1', interests: [] }
        };
        setItems([item, ...items]);
        setNewItems([item, ...newItems]);
        setNewInterest('');
      } else {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            text: `Interest ${newInterest} already exists`,
            severity: 'warning',
            autoHide: 2000
          }
        });
      }
    }
  };

  const closeDialog = () => {
    dispatch({
      type: Actions.HideConfirmation
    });
    handleClose();
  };

  const canAddNewInterest = (value: string): boolean => {
    let result = true;
    for (let i = 0; i < items.length; i++) {
      if (items[i].name.toUpperCase() === value.toUpperCase()) {
        result = false;
      }
    }
    return result;
  };

  const handleKeyPressed = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddInterestPressed();
    }
  };

  return (
    <DialogBox
      id="add-interest-to-category-title"
      width={886}
      title={'Add Interest'}
      onClose={handleClose}
    >
      <Box
        sx={{ height: '668px', display: 'flex', flexFlow: 'column', paddingY: '70px', gap: '50px' }}
      >
        <Box
          sx={{
            color: Colors.skyBlue,
            fontSize: '18px',
            lineHeight: '19px',
            fontWeight: 'bold',
            textAlign: 'center'
          }}
        >
          {name.replace(/([A-Z][a-z])/g, ' $1').replace(/(\d)/g, ' $1')}
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
            gap: '20px 24px',
            marginLeft: '13%',
            marginRight: '13%',
            flexGrow: 1,
            gridTemplateRows: 'max-content'
          }}
        >
          <InputComponent
            id="new-interest"
            label="Type Interest Name"
            value={newInterest}
            onChange={(e) => setNewInterest(e.currentTarget.value)}
            onKeyPress={handleKeyPressed}
            additionalStyle={{
              gridColumn: 'span 8'
            }}
          />
          <Button
            id="add-new-interest-button"
            data-testid="add-new-interest-button"
            sx={{
              width: 126,
              height: '36px',
              border: '1px solid ' + Colors.secondary,
              color: Colors.secondary,
              gridColumn: 'span 3'
            }}
            onClick={handleAddInterestPressed}
            disabled={loading}
          >
            Add Interest
          </Button>
          <Box
            sx={{
              gridColumn: 'span 12',
              display: 'flex',
              gap: '20px',
              marginTop: '50px',
              flexWrap: 'wrap',
              maxHeight: '30px'
            }}
          >
            {items.map((val, index) => (
              <Box key={index}>
                {val.isNew ? (
                  <Chip
                    id={`${name}-val`}
                    data-testid={`${val.name}-val`}
                    label={`${val.name}`}
                    sx={{
                      ...newChipStyles,
                      '.MuiChip-deleteIcon': deleteIconStyles
                    }}
                    onDelete={() => handleDeleteInterestPressed(val)}
                  />
                ) : (
                  <Chip
                    id={`${name}-val`}
                    data-testid={`${val.name}-val`}
                    label={`${val.name}`}
                    sx={chipStyles}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>
        <Box className="centralize">
          <CancelButton
            id="cancel-new-interest-to-category-button"
            disabled={loading}
            onClick={handleCancelPressed}
            text={'Cancel'}
          />
          <SaveButton
            id="save-new-interest-to-category-button"
            disabled={loading}
            onClick={handleSavePressed}
            text={'Save'}
          />
        </Box>
      </Box>
    </DialogBox>
  );
};

export default AddCategoryInterests;
